import { forkJoin, Subscription } from 'rxjs';
import { Component, inject, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { MarkdownFile, MarkdownVersion } from '../shared/models/markdown';
import { MarkdownsService } from '../shared/services/markdowns.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService, DialogRef, DialogCloseResult } from '@progress/kendo-angular-dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { pencilIcon, copyIcon, saveIcon, cancelIcon, fileAddIcon } from "@progress/kendo-svg-icons";
import { process, State } from '@progress/kendo-data-query';

enum EditorStatus {
  updating,
  creating,
  none
}

@Component({
    selector: 'app-agents-markdowns',
    templateUrl: './markdowns.component.html',
    styleUrls: ['./markdowns.component.scss'],
    standalone: false
})
export class MarkdownsComponent implements OnInit {
  readonly EditorStatus = EditorStatus;

  readonly editIcon = pencilIcon;
  readonly copyIcon = copyIcon;
  readonly saveIcon = saveIcon;
  readonly cancelIcon = cancelIcon;
  readonly plusIcon = fileAddIcon;

  @ViewChild('notificationContainer', { read: ViewContainerRef, static: true })
  notificationContainer: ViewContainerRef;

  private service = inject(MarkdownsService);
  private fb = inject(FormBuilder);
  private dialogService = inject(DialogService);
  private notificationService = inject(NotificationService);
  private clipboard = inject(Clipboard);

  gridView: GridDataResult;
  gridState: State = {
    skip: 0,
    take: 10,
    sort: []
  }

  allMarkdownFiles: MarkdownFile[] = [];
  availableVersions: MarkdownFile[] = [];

  isEditorVisible = false;
  isEditorReadonly = false;
  editorStatus = EditorStatus.none;

  editorForm: FormGroup;

  constructor() {
    this.editorForm = this.fb.group({
      filename: [{value: null, disabled: true}],
      selectedFile: [null],
      selectedVersion: [null],
      content: ['', Validators.required],
    });
  }

  private setUpdatingValidators() {
    this.editorForm.get('filename').setValidators(null);
    this.editorForm.get('selectedFile').setValidators(Validators.required);
    this.editorForm.get('selectedVersion').setValidators(Validators.required);
  }

  private setCreatingValidators() {
    this.editorForm.get('filename').setValidators([Validators.required, Validators.pattern(/^[a-zA-Z0-9_-]+$/)]);
    this.editorForm.get('selectedFile').setValidators(null);
    this.editorForm.get('selectedVersion').setValidators(null);
  }

  ngOnInit(): void {
    this.loadMarkdowns();
  }

  private loadMarkdowns(): void {
    this.service.getAll().subscribe({
      next: (data) => {
        this.allMarkdownFiles = data;
        this.gridView = process(this.allMarkdownFiles, this.gridState);
      },
      error: (err) => this.showNotification('error', 'Failed to load markdowns.'),
    });
  }

  dataStateChange(state: DataStateChangeEvent) {
    this.gridState = state;
    this.gridView = process(this.allMarkdownFiles, this.gridState);
  }

  public changeStatus(dataItem: MarkdownFile, newStatus: boolean): void {
    this.service.changeStatus(dataItem.id, newStatus).subscribe({
      next: () => {
        dataItem.enabled = newStatus;
        this.showNotification('success', 'Status updated successfully.');
        this.loadMarkdowns();
      },
      error: (err) => {
        this.showNotification('error', 'Failed to update status.');
        this.loadMarkdowns();
      },
    });
  }

  public deleteHandler(dataItem: MarkdownFile): void {
    const dialog: DialogRef = this.dialogService.open({
      title: 'Please confirm',
      content: `Are you sure you want to delete ${dataItem.itemName}?`,
      actions: [{ text: 'No' }, { text: 'Yes', themeColor: 'primary' }],
      width: 450,
      height: 200,
      minWidth: 250,
    });

    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult || result.text === 'No') {
        return;
      }

      this.service.delete(dataItem.id).subscribe({
        next: () => {
          this.showNotification('success', 'Markdown deleted successfully.');
          this.loadMarkdowns();
          this.editorForm.reset();
          this.isEditorVisible = false;
          this.editorStatus = EditorStatus.none;
          this.availableVersions = [];
        },
        error: (err) => this.showNotification('error', 'Failed to delete markdown.'),
      });
    });
  }

  public createHandler(): void {
    this.isEditorVisible = true;
    this.isEditorReadonly = false;
    this.editorForm.reset();
    this.editorStatus = EditorStatus.creating;
    this.editorForm.get('filename').enable();
    this.setCreatingValidators();
  }

  public editHandler(selectedFile: MarkdownFile): void {
    forkJoin({
      details: this.service.getById(selectedFile.id),
      versions: this.service.getVersions(selectedFile.id),
    }).subscribe({
      next: ({ details, versions }) => {
        this.isEditorVisible = true;
        this.isEditorReadonly = false;
        this.availableVersions = versions;
        this.editorStatus = EditorStatus.updating;

        const latestVersion = this.availableVersions.find((v) => v.id === selectedFile.latestMdVersionId);

        this.editorForm.setValue({
          filename: selectedFile.itemName,
          selectedFile: selectedFile,
          selectedVersion: latestVersion,
          content: details.content,
        });
        this.editorForm.get('filename').disable();
        this.setUpdatingValidators();
      },
      error: (err) => this.showNotification('error', 'Failed to load markdown details.'),
    });
  }

  public onVersionChange(version: MarkdownFile): void {
    const selectedFile = this.editorForm.get('selectedFile')?.value;
    if (!selectedFile) return;
    this.service.
    getByVersionId(selectedFile.id, version.id).subscribe({
      next: (markdownFile) => {
        this.isEditorReadonly = version.id !== selectedFile.latestMdVersionId;
        this.editorForm.patchValue({
          selectedVersion: version,
          content: markdownFile.content,
        });
      },
      error: (err) => this.showNotification('error', 'Failed to load markdown version.'),
    });
  }

  public saveHandler(): void {
    if (this.editorForm.invalid) return;

    if (this.editorStatus === EditorStatus.updating) {
      const {selectedFile, content} = this.editorForm.value;
      this.service.update(selectedFile, content).subscribe({
        next: () => {
          this.showNotification('success', 'Markdown saved successfully.');
          this.isEditorVisible = false;
          this.isEditorReadonly = false;
          this.editorForm.reset();
          this.availableVersions = [];
          this.editorStatus = EditorStatus.none;
          this.loadMarkdowns();
        },
        error: (err) => this.showNotification('error', 'Failed to save markdown.'),
      });
    } else if (this.editorStatus === EditorStatus.creating) {
      const { filename, content } = this.editorForm.value;
      this.service.create(filename, content).subscribe({
        next: () => {
          this.showNotification('success', 'Markdown created successfully.');
          this.loadMarkdowns();
          this.isEditorVisible = false;
          this.isEditorReadonly = false;
          this.editorForm.reset();
          this.availableVersions = [];
          this.editorStatus = EditorStatus.none;
        },
        error: (err) => this.showNotification('error', 'Failed to create markdown.'),
      });
    }
  }

  public copyContent(): void {
    const content = this.editorForm.get('content')?.value;
    if (this.clipboard.copy(content)) {
      this.showNotification('success', 'Content copied to clipboard.');
    } else {
      this.showNotification('error', 'Failed to copy content.');
    }
  }

  private showNotification(type: 'success' | 'error', message: string): void {
    this.notificationService.show({
      content: message,
      appendTo: this.notificationContainer,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
      hideAfter: 3000,
    });
  }

}
